import React, { ReactElement } from 'react';
import { t, Trans } from '@lingui/macro';
import { Agreement, log, User } from '@economic/react-app';

import { Badge, Color, Spinner, State } from '@economic/taco';
import { format } from 'date-fns';
import { Table3ColumnSort, Table3RowHeight } from '@economic/taco/dist/components/Table3/types';
import { formatISODate } from '~/app/shared/util/utilities/date';
import internalApiClient from '~/app/shared/util/api/internalApiClient';
import {
    EntryTypeNamesEnum,
    Journal,
    SmartPayPaymentAccountModel,
    SmartPayPaymentCountByStatusModel,
    SmartPayPaymentExtendedModel,
    SmartPayPaymentResultModel,
} from '~/types/internal-api';
import { PaymentTypes, SelectableAccount } from '~/types';
import getErrorMessage from '../../../journals/draft-entries/JournalEntryErrors';
import {
    CountAndTotal,
    EXCLUDE_PAYMENTS_BATCH_SIZE,
    PaymentStatuses,
    SmartPayHandledPayment,
    SmartPayPaymentAccountStatus,
    SmartPayPaymentExcludeType,
    SmartPayPaymentsDefaultFilters,
    SmartPayPaymentsFilters,
    SmartPayRemovePayload,
    smartPayValidAccountTypes,
} from '../types';

// #region validate journal and fee accounts
export const getValidFeeAccountsForSmartPay = (feeAccounts: SelectableAccount[] | undefined): SelectableAccount[] =>
    feeAccounts?.filter(fa => smartPayValidAccountTypes.includes(fa.type.number) && fa.access && !fa.isBlockedForDirectEntries) ??
    [];

export const getValidJournalsForSmartPay = (journals: Journal[] | undefined): Journal[] =>
    journals?.filter(j => j.allowedEntryType === EntryTypeNamesEnum.All) ?? [];

export const isSelectedJournalInvalidForSmartPay = (journal: Journal | undefined): boolean =>
    journal?.allowedEntryType !== EntryTypeNamesEnum.All;
// #endregion

// #endregion general
export const isPaymentDomestic = (payment: SmartPayPaymentExtendedModel | null): boolean => {
    if (!payment) return false;
    return payment?.baseCurrency === payment?.invoiceCurrency;
};

export const isPaymentEuro = (payment: SmartPayPaymentExtendedModel | null): boolean => payment?.invoiceCurrency === 'EUR';

export const isPaymentDomesticOrEuro = (payment: SmartPayPaymentExtendedModel | null): boolean =>
    !payment ? false : isPaymentDomestic(payment) || isPaymentEuro(payment);

export const doesPaymentHaveMatches = (payment: SmartPayPaymentExtendedModel | null): boolean =>
    !payment ? false : payment.draftRemainingAmount !== payment.invoiceAmount;

export const hasPaymentAmountBeenChangedManually = (payment: SmartPayPaymentExtendedModel | null) =>
    !payment ? false : payment.draftRemainingAmount !== payment.amountToBePaid;

export const hasPaymentAmountBeenChangedAfterCreation = (payment: SmartPayPaymentExtendedModel | null) =>
    !payment ? false : payment.paidAmount !== payment.amountToBePaid;

// check that covers if invoice has been removed or at least one of the combined invoices has been removed
export const hasInvoiceAmountBeenChanged = (payment: SmartPayPaymentExtendedModel | null) =>
    !payment ? false : payment.paidAmount !== payment.actualPaidAmount;

export const getPaidFee = (paymentDetails: SmartPayHandledPayment): number => {
    return paymentDetails.fee + paymentDetails.recipientFeeAmount;
};

export const isN1ApprovalUrlUnavailable = (paymentsWithApproveErrorsRefs: SmartPayPaymentResultModel[]): boolean =>
    paymentsWithApproveErrorsRefs.some(payment =>
        payment.errorMessages.some(err => err.message === 'SMARTPAYPAYMENT_PAYMENT_GET_APPROVE_URL_NO_CONTENT')
    );

// #endregion

// #region popups
export const openInvoiceWindow = (attachmentNumber: number) => {
    const { ECONOMIC_PATH } = process.env;

    window.open(
        `${ECONOMIC_PATH}/scanning/Viewer.aspx?id=${attachmentNumber}`,
        undefined,
        'popup,left=100,top=100,width=700,height=700'
    );
};

export const openBalanceWindow = (setupId: number, supplierId: number) => {
    const { ECONOMIC_PATH } = process.env;

    window.open(
        `${ECONOMIC_PATH}/generelt/dataedit.asp?form=50&ops=${setupId}&popup=yes&id=${supplierId}&Kladde=1`,
        undefined,
        'popup,left=100,top=100,width=1000,height=700'
    );
};

// #endregion

// #region GET payments

const generateSearchInputQuery = (searchInputValue: string) => {
    const queryParams = [
        'supplierName',
        'supplierId',
        'paymentReference',
        'invoiceNumber',
        'invoiceAmount',
        'amountToBePaid',
        'amountInBaseCurrency',
        'draftRemainingAmount',
        'paidAmount',
        'invoiceTotalInBaseCurrency',
        'invoiceCurrency',
        'paymentTypeId',
    ];

    const getMatchingPaymentTypesIds = searchValue => {
        const paymentTypes = [
            {
                id: PaymentTypes.Bank,
                text: t`PaymentType_Bank`.toLowerCase(),
            },
            {
                id: PaymentTypes.Entry,
                text: t`PaymentType_Entry`.toLowerCase(),
            },
            {
                id: PaymentTypes.Iban,
                text: t`PaymentType_IBAN`.toLowerCase(),
            },
            {
                id: PaymentTypes.None,
                text: t`PaymentType_None`.toLowerCase(),
            },
            {
                id: PaymentTypes.Plus04,
                text: t`PaymentType_+04`.toLowerCase(),
            },
            {
                id: PaymentTypes.Plus71,
                text: t`PaymentType_+71`.toLowerCase(),
            },
            {
                id: PaymentTypes.Plus73,
                text: t`PaymentType_+73`.toLowerCase(),
            },
            {
                id: PaymentTypes.Plus75,
                text: t`PaymentType_+75`.toLowerCase(),
            },
        ];

        const matchingPaymentTypes = paymentTypes.filter(pt => pt.text.includes(searchValue.toLowerCase()));

        return matchingPaymentTypes.map(mpt => mpt.id);
    };

    const initialValue: string[] = [];
    const searchInputQuery = queryParams
        .reduce((acc, curr) => {
            if (
                [
                    'supplierId',
                    'invoiceAmount',
                    'amountToBePaid',
                    'amountInBaseCurrency',
                    'draftRemainingAmount',
                    'paidAmount',
                    'invoiceTotalInBaseCurrency',
                ].includes(curr) &&
                isNaN(+searchInputValue)
            )
                return [...acc];
            if (curr === 'paymentTypeId') return [...acc, `paymentTypeId$in:[${getMatchingPaymentTypesIds(searchInputValue)}]`];
            return [...acc, `${curr}$like:${searchInputValue}`];
        }, initialValue)
        .join('$or:');

    return searchInputQuery;
};

export const getPaymentsQuery = (
    searchInput: string,
    appliedFilters: SmartPayPaymentsFilters,
    availableSuppliersCount: number,
    availableSupplierGroupsCount: number,
    availableStatuses: PaymentStatuses[],
    availableSenderBankAccountCount: number,
    journalsLength?: number,
    pageIndex?: number,
    pageSize?: number,
    sorting?: Table3ColumnSort[],
    createdPayments = false,
    returnAll = false
) => {
    const filterQuery = generateFilterQuery(
        appliedFilters,
        availableSuppliersCount,
        availableSupplierGroupsCount,
        availableStatuses,
        availableSenderBankAccountCount,
        journalsLength
    );

    const route = '/smartpay/payment?';

    let paginationAndSortingQuery = '';

    if (pageIndex !== undefined && pageSize !== undefined) {
        paginationAndSortingQuery += `&pageSize=${pageSize}&skipPages=${pageIndex}`;
    } else if (returnAll) {
        paginationAndSortingQuery += `&returnAll=true`;
    }

    if (sorting !== undefined) {
        paginationAndSortingQuery += `${getPaymentSortingRule(sorting)}`;
    }

    if (searchInput.length === 0)
        return `${route}calculateTotalCount=true${filterQuery}${paginationAndSortingQuery}&createdPayments=${createdPayments}`;

    const searchInputQuery = generateSearchInputQuery(searchInput);

    return `${route}calculateTotalCount=true${filterQuery}$and:(${searchInputQuery})${paginationAndSortingQuery}&createdPayments=${createdPayments}`;
};

export const fetchPayments = async (
    queryPath: string,
    loadingSetter: React.Dispatch<React.SetStateAction<boolean>>
): Promise<{ data: SmartPayPaymentExtendedModel[]; length: number }> => {
    loadingSetter(true);

    const { data: responseData } = await internalApiClient.get(queryPath);

    loadingSetter(false);

    return {
        data: responseData.data,
        length: responseData.pagination.count,
    };
};

export const excludePayments = (
    payments: SmartPayPaymentExtendedModel[],
    type: SmartPayPaymentExcludeType
): Promise<unknown>[] => {
    const getPayload = (payments: SmartPayPaymentExtendedModel[], type: SmartPayPaymentExcludeType): SmartPayRemovePayload[] => {
        return payments.map(p => ({
            journalId: p.journalId,
            draftEntryId: p.draftEntryId,
            bookedEntryId: p.bookedEntryId,
            excludeType: type,
        }));
    };

    const excludePaymentsRequest = async (data: SmartPayRemovePayload[]) => {
        try {
            const response = await internalApiClient.post(`smartpay/payment/remove`, data);
            return response;
        } catch (e) {
            return e;
        }
    };

    const batchPaymentsPayloads = (payloads: SmartPayRemovePayload[]): SmartPayRemovePayload[][] =>
        payloads.reduce((result: SmartPayRemovePayload[][], _, index: number) => {
            if (index % EXCLUDE_PAYMENTS_BATCH_SIZE === 0) {
                result.push(payloads.slice(index, index + EXCLUDE_PAYMENTS_BATCH_SIZE));
            }
            return result;
        }, []);

    const requestsPayloads: SmartPayRemovePayload[][] = batchPaymentsPayloads(getPayload(payments, type));

    return requestsPayloads.map(payload => excludePaymentsRequest(payload));
};
// #endregion

// #region payments filters & sorting

export const hasAppliedFilters = (filters: SmartPayPaymentsFilters): boolean => {
    if (
        filters.status === '' &&
        filters.includeDaybooks === false &&
        filters.supplier === '' &&
        Object.keys(filters).length === 3
    )
        return false;
    return true;
};

export const getPaymentSortingRule = (sorting: Table3ColumnSort[]): string => {
    if (sorting.length === 0) return '';
    return `&sort=${sorting[0].desc ? '-' : ''}${sorting[0].id}`;
};

export const getDefaultFilters = (): SmartPayPaymentsDefaultFilters => {
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth();
    const currentYear = currentDate.getFullYear();
    let yearPeriodName = currentYear.toString();
    const lastYear = currentYear - 1;
    const nextYear = currentYear + 1;
    const january = 0;
    const november = 10;
    const december = 11;
    let fromDate = new Date(currentYear, january, 1);
    let toDate = new Date(currentYear, december, 31);

    if (currentMonth === november || currentMonth === december) {
        fromDate = new Date(currentYear, january, 1);
        toDate = new Date(nextYear, december, 31);
        yearPeriodName += `-${november.toString()}`;
    } else if (currentMonth === january) {
        fromDate = new Date(lastYear, december, 1);
        toDate = new Date(currentYear, december, 31);
        yearPeriodName += `-${january.toString()}`;
    }

    return {
        yearSpecialPeriodName: yearPeriodName,
        filter: {
            fromDate,
            toDate,
            includeDaybooks: true,
            status: '',
            supplier: '',
            supplierGroup: '',
            senderBankAccount: '',
            journalIds: '',
        },
    };
};

const generateFilterQuery = (
    appliedFilters: SmartPayPaymentsFilters,
    supplierListLength: number,
    supplierGroupListLength: number,
    availableStatuses: PaymentStatuses[],
    senderBankAccountListLength: number,
    journalIdsLength?: number
): string => {
    const generateKeyQuery = (filterKey: string, value: any): string | undefined => {
        switch (filterKey) {
            case 'fromDate':
                return `paymentDate$gte:${formatISODate(value)}`;
            case 'toDate':
                return `paymentDate$lte:${formatISODate(value)}`;
            case 'supplier':
                if (value.length === 0) return;
                return `supplierId$in:[${value}]`;
            case 'supplierGroup':
                if (value.length === 0) return;
                return `supplierGroupId$in:[${value}]`;
            case 'status':
                if (value.length === 0) return;

                let statusFilterValue = value;

                // Pending & Pending_Approval are treated together
                if (statusFilterValue.includes(PaymentStatuses.Pending))
                    statusFilterValue += `,${PaymentStatuses.PendingApproval}`;

                // Failed status covers multiple
                if (statusFilterValue.includes(PaymentStatuses.Failed))
                    statusFilterValue += `,${PaymentStatuses.TradeReverted},${PaymentStatuses.TransferForbidden},${PaymentStatuses.Canceled}`;

                return `status$in:[${statusFilterValue}]`;
            case 'includeDaybooks':
                if (value === true) return;
                return `isDaybook$eq:${value}`;
            case 'journalIds':
                if (value === '') return;
                return `journalId$in:[${value}]`;
            case 'senderBankAccount':
                if (value === '') return;
                return `senderBankAccount$in:[${value}]`;
            default:
        }
    };

    const optimizedFilters = {
        ...appliedFilters,
        // remove supplier, status and senderBankAccount filters if all options are selected
        supplier: appliedFilters.supplier.split(',').length === supplierListLength ? '' : appliedFilters.supplier,
        supplierGroup: appliedFilters.supplierGroup
            ? appliedFilters.supplierGroup.split(',').length === supplierGroupListLength
                ? ''
                : appliedFilters.supplierGroup
            : '',
        status: appliedFilters.status === '' ? availableStatuses.toString() : appliedFilters.status,
        senderBankAccount:
            appliedFilters.senderBankAccount?.split(',').length === senderBankAccountListLength
                ? ''
                : appliedFilters.senderBankAccount,
        journalIds:
            !appliedFilters.journalIds ||
            !appliedFilters.includeDaybooks ||
            [0, journalIdsLength].includes(appliedFilters.journalIds.split(',').length)
                ? ''
                : appliedFilters.journalIds,
    };

    const filterKeys = Object.keys(optimizedFilters);
    if (filterKeys.length === 0) return '';

    const queryParameters = filterKeys.map(key => generateKeyQuery(key, optimizedFilters[`${key}`])).filter(qp => qp);

    return `&filter=${queryParameters.join('$and:')}`;
};

// #endregion

export const canUserTakeBackOrRemoveScheduledPayments = (user: User) => user.isAdministrator || user.isSuperUser;

// #region take payment back
export const canPaymentBeTakenBack = (payment: SmartPayPaymentExtendedModel) => payment.status === PaymentStatuses.Scheduled;

export const takePaymentsBack = async (smartPayPaymentIds: { SmartPayPaymentId: number }[]) => {
    try {
        const response = await internalApiClient.post(`smartpay/payment/takeBack`, smartPayPaymentIds);
        return response;
    } catch (error) {
        return error;
    }
};
// #endregion

// #region retry create entries
export const canRetryCreateEntries = (payment: SmartPayPaymentExtendedModel) =>
    payment.status === PaymentStatuses.MissingEntries && payment.postValidationMessages.length > 0;

const getMissingEntriesError = (errorCode: string, param?: string) => {
    let message = getErrorMessage({ error: errorCode });
    if (message === 'An error occured while saving the entry.') {
        message = getPaymentErrorTranslation(errorCode, param);
    }
    return message;
};

export const retryCreateEntries = async (payment: SmartPayPaymentExtendedModel) => {
    try {
        await internalApiClient.put(`smartpay/payment/${payment.paymentReference}/retryCreateEntries`);
    } catch (error) {
        if (error.response.data.message) {
            return getMissingEntriesError(error.response.data.message, error.response.data.value);
        }
        return error.response.data;
    }
};

export const canMarkAsUnpaid = (payment: SmartPayPaymentExtendedModel) => payment.status === PaymentStatuses.MarkedAsPaid;

export const markAsUnpaid = async (payments: { journalId?: number; draftEntryId?: number; bookedEntryId?: number }[]) => {
    try {
        await internalApiClient.post(`smartpay/payment/undoRemove`, payments);
        return true;
    } catch (error: any) {
        if (error.response.data.message) {
            let message = getErrorMessage({ error: error.response.data.message });
            if (message === 'An error occured while saving the entry.') {
                message = getPaymentErrorTranslation(error.response.data.message, error.response.data.value);
            }
            return message;
        }
        return error.response.data;
    }
};

export const getMissingEntriesErrorTranslation = (errorCodes: string[]): string | ReactElement => {
    const reason = Array.from(new Set(errorCodes.map(e => getMissingEntriesError(e))))
        .join(' ')
        .trim()
        .replace(/\.$/, '');
    return t`We couldn't automatically create the entries for this payment. The reason: ${reason}. Click on the 'Retry' button to create the entries.`;
};
// #endregion

// #region handled payment details
export const getHandledPaymentData = async (paymentReference: string) => {
    try {
        return await internalApiClient.get(`smartpay/payment/${paymentReference}`);
    } catch (error) {
        return error;
    }
};

export const getOrphanedPaymentData = async (smartPayPaymentId: number) => {
    try {
        return await internalApiClient.get(`smartpay/payment/orphaned/${smartPayPaymentId}`);
    } catch (error) {
        return error;
    }
};

export const getHandledPaymentTooltipText = (paymentDetails: SmartPayHandledPayment): string | null => {
    if (paymentDetails.approvals.length > 0) {
        return t`Approved by ${paymentDetails.approvals.reduce(
            (acc, curr, i) => acc + `${curr.approvedByEmail}${i !== paymentDetails.approvals.length - 1 ? ', ' : ''}`,
            ''
        )}`;
    }

    if (paymentDetails.rejectionReason || paymentDetails.rejectedByEmail) {
        return t`Rejected by ${paymentDetails.rejectedByEmail}${
            paymentDetails.rejectionReason ? `: ${paymentDetails.rejectionReason}` : ''
        }`;
    }

    return null;
};
// #endregion

// #region statuses

export const getPaymentStatusBadge = (status: PaymentStatuses): JSX.Element => {
    const getBadgeColor = (status: string): Color => {
        switch (status) {
            case PaymentStatuses.New:
            case PaymentStatuses.Creating:
                return 'blue';
            case PaymentStatuses.Pending:
            case PaymentStatuses.PendingApproval:
            case PaymentStatuses.PartiallyApproved:
                return 'yellow';
            case PaymentStatuses.Scheduled:
            case PaymentStatuses.Processing:
            case PaymentStatuses.Completed:
                return 'green';
            case PaymentStatuses.TradeReverted:
            case PaymentStatuses.TransferForbidden:
            case PaymentStatuses.Canceled:
            case PaymentStatuses.Failed:
            case PaymentStatuses.Rejected:
            case PaymentStatuses.Invalid:
            case PaymentStatuses.MissingEntries:
                return 'red';
            case PaymentStatuses.MarkedAsPaid:
                return 'brown';
            case PaymentStatuses.CreditNote:
                return 'purple';
            default:
                return 'blue';
        }
    };

    return (
        <Badge status subtle color={getBadgeColor(status)}>
            {getPaymentStatusesTranslations(status)}{' '}
            {status === PaymentStatuses.Creating && <Spinner className="ml-2" delay={0} />}
        </Badge>
    );
};

// #endregion

// #region translations
export const getPaymentTypeTranslation = (paymentTypeId: number): string => {
    switch (paymentTypeId) {
        case PaymentTypes.None:
            return t`PaymentType_None`;
        case PaymentTypes.Plus71:
            return t`PaymentType_+71`;
        case PaymentTypes.Plus73:
            return t`PaymentType_+73`;
        case PaymentTypes.Plus04:
            return t`PaymentType_+04`;
        case PaymentTypes.Bank:
            return t`PaymentType_Bank`;
        case PaymentTypes.Iban:
            return t`PaymentType_IBAN`;
        case PaymentTypes.Entry:
            return t`PaymentType_Entry`;
        case PaymentTypes.Plus75:
            return t`PaymentType_+75`;
        default:
            return '';
    }
};

export const getPaymentStatusesTranslations = (status: PaymentStatuses): string => {
    switch (status) {
        case PaymentStatuses.New:
            return t`Ready`;
        case PaymentStatuses.Creating:
            return t`Creating`;
        case PaymentStatuses.Pending:
        case PaymentStatuses.PendingApproval:
            return t`Pending`;
        case PaymentStatuses.PartiallyApproved:
            return t`Partially approved`;
        case PaymentStatuses.Rejected:
            return t`Rejected`;
        case PaymentStatuses.Scheduled:
            return t`Scheduled`;
        case PaymentStatuses.Processing:
            return t`Transferring`;
        case PaymentStatuses.Completed:
            return t`Completed`;
        case PaymentStatuses.Invalid:
            return t`Invalid`;
        case PaymentStatuses.TradeReverted:
        case PaymentStatuses.TransferForbidden:
        case PaymentStatuses.Canceled:
        case PaymentStatuses.Failed:
            return t`Failed`;
        case PaymentStatuses.CreditNote:
            return t`Credit Note`;
        case PaymentStatuses.MissingEntries:
            return t`Missing entries`;
        case PaymentStatuses.MarkedAsPaid:
            return t`Marked as paid`;
        default:
            log.error(new Error('smartpay-payments.unhandled-status-received'), { status });
            return '';
    }
};

export const getFailedStatusTooltipTranslation = (status: PaymentStatuses): string => {
    switch (status) {
        case PaymentStatuses.TradeReverted:
            return t`Trade Reverted`;
        case PaymentStatuses.TransferForbidden:
            return t`Transfer Forbidden`;
        case PaymentStatuses.Canceled:
            return t`Canceled`;
        default:
            return '';
    }
};

export const getPaymentSupplierIdentifierLabel = paymentTypeId => {
    switch (paymentTypeId) {
        case PaymentTypes.Bank:
            return t`Account no.`;
        case PaymentTypes.Plus71:
        case PaymentTypes.Plus73:
        case PaymentTypes.Plus75:
            return t`FI-Kreditor No.`;
        case PaymentTypes.Plus04:
            return t`Giro Account`;
        case PaymentTypes.Iban:
            return t`IBAN/SWIFT no`;
        default:
            '';
    }
};

export const getPaymentSupplierInvoiceIdentifierLabel = paymentTypeId => {
    switch (paymentTypeId) {
        case PaymentTypes.Bank:
            return t`Message`;
        case PaymentTypes.Plus71:
            return t`OCR line`;
        case PaymentTypes.Iban:
        case PaymentTypes.Plus73:
            return t`Message`;
        case PaymentTypes.Plus75:
            return t`OCR line/Message`;
        case PaymentTypes.Plus04:
            return t`OCR line`;
        default:
            '';
    }
};

export const getIdentifiersErrorMessage = errorData =>
    errorData.errorCode
        ? (getPaymentErrorTranslation(errorData.errorCode, `${errorData.data.length}`) as string)
        : (getPaymentErrorTranslation(errorData) as string);

export const getPaymentErrorTranslation = (errorCode: string, param?: string): string | ReactElement => {
    switch (errorCode) {
        // PRE VALIDATION
        case 'SMARTPAYPAYMENT_SUPPLIER_STREET_EMPTY':
            return t`Supplier street name is not set`;
        case 'SMARTPAYPAYMENT_SUPPLIER_POSTCODE_EMPTY':
            return t`Supplier postcode is not set`;
        case 'SMARTPAYPAYMENT_SUPPLIER_CITY_EMPTY':
            return t`Supplier city is not set`;
        case 'SMARTPAYPAYMENT_SUPPLIER_COUNTRY_INVALID':
            return t`Supplier country is invalid`;
        case 'SMARTPAYPAYMENT_CURRENCY_NOT_SUPPORTED':
            return t`Payments in this currency cannot be made through Smart Pay.`;
        case 'SMARTPAYPAYMENT_PAYMENT_TYPE_INVALID':
            return t`Selected payment type invalid`;
        case 'SMARTPAYPAYMENT_INTERNATIONAL_PAYMENT_TYPE_SHOULD_BE_IBAN':
            return t`Select IBAN for international payments`;
        case 'SMARTPAYPAYMENT_LOCAL_PAYMENT_IBAN_NOT_SUPPORTED':
            return t`IBAN payment type is not supported for DKK payments.`;
        case 'SMARTPAYPAYMENT_INTERNATIONAL_PAYMENT_IBAN_MISSING':
            return t`This field is required.`;
        case 'SMARTPAYPAYMENT_INTERNATIONAL_PAYMENT_IBAN_SWIFT_MISSING':
            return t`The IBAN/SWIFT field must contain the IBAN and SWIFT code separated by '/'`;
        case 'FI_SUPPLIER_NUMBER_VALIDATION_MESSAGE':
            return t`FI Supplier Number validation failed. Must be 8 digits.`;
        case 'GIRO_ACCOUNT_VALIDATION_MESSAGE':
            return t`It may start with GIRO (optional) and must have 2-10 digits afterward`;
        case 'ACCOUNT_NUMBER_VALIDATION_MESSAGE':
            return t`It should have 11 or 14 digits and may have a '-' after the 4th digit (optional)`;
        case 'IBAN_OR_SWIFT_VALIDATION_MESSAGE':
            return t`Supplier IBAN/SWIFT validation failed`;
        case 'SMARTPAYPAYMENT_PAYMENT_PAYMENTID_ERROR_MUST_BE_EXACT_LENGTH':
            return t`Payment ID lenght is wrong`;
        case 'SMARTPAYPAYMENT_PAYMENT_PAYMENTID_ERROR_MUST_BE_MIN_LENGTH':
            return t`Payment ID too short`;
        case 'SMARTPAYPAYMENT_PAYMENT_PAYMENTID_ERROR_MUST_BE_MAX_LENGTH':
            return t`Payment ID too long`;
        case 'SMARTPAYPAYMENT_PAYMENT_PAYMENTID_MUST_BE_NUMERIC':
            return t`Payment ID must be numeric`;
        case 'PAYMENTS_PAYMENTTYPES_CREDITORID_FI75_NOT_VALIDATED':
            return t`'OCR/optional message' must be a number (2-16 digits) or use the format 'number/text'.`;
        case 'SMARTPAYPAYMENT_PAYMENT_DATE_INVALID':
            return t`Payment date is in the past or set to a bank holiday`;

        // INFO ERRORS
        case 'INTEGRATION_NOT_FOUND':
            return t`Please onboard to Smart Pay to be able to use this feature`;
        case 'INTEGRATION_NOT_COMPLETED':
            return t`Smart Pay onboarding not completed. Please finish onboarding to Smart Pay to be able to use this feature`;
        case 'INTEGRATION_API_KEY_NOT_SET':
        case 'INTEGRATION_API_KEY_INVALID':
            return t`There is an issue with your Smart Pay integration. Please reach out to our Support.`;
        case 'SMARTPAY_INFO_MISSING_DATA':
            return (
                <div>
                    <Trans>
                        Please note that you can do payments only when a bank account with Mastercard Overførselsservice is
                        selected in your November First settings. Learn more about it{' '}
                        <a
                            href="https://www.e-conomic.dk/support/artikler/underskriv-din-aftale-med-mastercard-overfoerselsservice"
                            target="_blank"
                            rel="noreferrer">
                            here.
                        </a>
                    </Trans>
                </div>
            );
        case 'SMARTPAY_INFO_UNKNOWN_ERROR':
            return t`Something went wrong. Please refresh the page. If you still get this message afterwards, please contact Support`;

        // POST VALIDATION
        case 'SMARTPAYPAYMENT_AMOUNT_NOT_POSITIVE':
            return t`Payment amount is negative`;
        case 'SMARTPAYPAYMENT_JOURNALENTRYPAYMENT_NOT_FOUND':
            return t`Journal entry not found`;
        case 'SMARTPAYPAYMENT_SUPPLIER_NOT_FOUND':
            return t`Supplier not found`;
        case 'SMARTPAYPAYMENT_JOURNALENTRYPAYMENT_ALREADY_PAID':
            return t`Already paid`;
        case 'SMARTPAYPAYMENT_JOURNALENTRYPAYMENT_CREATING':
            return t`The payment is currently creating`;
        case 'SMARTPAYPAYMENT_AMOUNT_TOO_BIG':
            return t`Payment amount too big`;
        case 'InvalidCurrency':
            return t`Selected currency invalid`;
        case 'InvalidBIC':
        case 'InvalidBic':
            return t`BIC invalid`;
        case 'NullRecipientBic':
            return t`SWIFT/BIC not found. Please check the IBAN is valid or add the SWIFT/BIC code.`;
        case 'MissingRecipientName':
            return t`Recipient name not provided`;
        case 'MissingRecipientAddress':
            return t`Recipient address not provided`;
        case 'InvalidEmailAddress':
            return t`Email address invalid`;
        case 'CustomerCreditLimitExceeded':
            return t`Credit limit exceeded`;
        case 'CurrencyCrossNotSupported':
            return t`Currency not supported`;
        case 'InvalidCountryCode':
            return t`The provided country code is invalid`;
        case 'SEPA_CannotPayRecipientFees':
            return t`Recipient does not pay the fee`;
        case 'SEPAPaymentRequiresIBAN':
            return t`Please provide a valid IBAN for SEPA payments`;
        case 'Bic_CountryMisMatch':
        case 'IBAN_CountryMisMatch':
            return t`IBAN and BIC are not from the same country`;
        case 'EmptyAccountNumber':
            return t`Account number is empty`;
        case 'InvalidIBAN':
            return t`IBAN invalid`;
        case 'InvalidDanishAccount':
            return t`The Danish recipient account is invalid. It has the be 11 or 14 digits long`;
        case 'TransactionDateNotSupported':
            return t`Transaction Date not available`;
        case 'NonNetsLocalPayment':
            return t`The provided sender bank account does not have NETS Overførselservice enabled.`;
        case 'FromCurrencyDifferentFromPayerBankAccountCurrency':
            return t`Sender currency does not match recipient currency`;
        case 'InvalidFikocrLine':
            return t`FIK number is invalid.`;
        case 'BankAccountIsNotNetsBankAccount':
            return t`The provided sender bank account does not have NETS Overførselservice enabled.`;
        case 'PaymentRequiresApproval':
            return t`Payment must be approved first`;
        case 'MissingBankAccount':
            return t`Bank account missing`;
        case 'RecipientAccountNumber':
            return t`Recipient account number invalid`;
        case 'SMARTPAYPAYMENT_PAYMENT_AMOUNT_NEGATIVE':
            return t`The Payment Amount should be positive.`;
        case 'SMARTPAYPAYMENT_DRAFT_RECONCILIATION_AMOUNT_NEGATIVE':
            return t`The matched amount is too high. Double check the draft payments/credit notes matched on this entry.`;
        case 'SMARTPAYPAYMENT_PAYMENTACCOUNTID_INVALID':
            return t`Payment Account ID Invalid`;
        case 'SMARTPAYPAYMENT_PAYMENT_GET_APPROVE_URL_GENERIC_ERROR':
        case 'SMARTPAYPAYMENT_PAYMENT_RETRY_CREATE_ENTRIES_GENERIC_ERROR':
            return t`Something went wrong. Please try again. If it still happens, contact Support and provide this code: ${param}`;
        case 'SMARTPAYPAYMENT_PAYMENT_APPROVE_INVALID_STATUS':
            return 'Cannot approve payment with invalid status.';
        case 'SMARTPAYPAYMENT_PAYMENT_GET_APPROVE_URL_FAILED':
            return 'Error in redirecting to November First. Try again.';
        case 'SepaCannotPayRecipientFees':
        // FIXME: duplicate case label
        // eslint-disable-next-line no-duplicate-case,no-fallthrough
        case 'CurrencyCrossNotSupported':
            return t`The recipient fee cannot be paid. Try again or untoggle the recipient fee for this payment.`;
        case 'IbanBicCountryMisMatch':
            return t`The SWIFT code does not match the IBAN and the country. Ensure the payment details are correct.`;

        // TAKE PAYMENT BACK
        case 'SMARTPAYPAYMENT_PAYMENT_NOT_FOUND':
            return t`Payment not found. Please refresh page and try again`;
        case 'SMARTPAYPAYMENT_PAYMENT_STATUS_INVALID_FOR_TAKE_BACK':
            return t`Only Scheduled payments can be taken back`;
        case 'PaymentCannotBeReverted':
            return t`Payment cannot be taken back. Please refresh page and try again`;
        // EXCLUDE
        case 'SMARTPAYPAYMENT_REMOVE_PAYMENT_STATUS_INVALID':
            return t`Invalid status. Please refresh page and try again`;
        case 'SMARTPAYPAYMENT_REMOVE_CREDIT_NOTE_CANNOT_BE_MARKED_AS_PAID':
            return t`Credit notes cannot be marked as paid. You can use the Remove action if you want to exclude them from this view.`;
        // EDIT PAYMENT
        case 'JOURNAL_ENTRY_PAYMENT_STATUS_INVALID_FOR_UPDATE':
            return t`Cannot update payments with this status`;
        case 'JOURNAL_ENTRY_PAYMENT_TYPE_INVALID':
            return t`Cannot find selected payment type`;
        case 'JOURNAL_ENTRY_PAYMENT_AMOUNT_SHOULD_BE_POSITIVE':
            return t`Payment amount should be positive`;
        case 'JOURNAL_ENTRY_PAYMENT_AMOUNT_SHOULD_BE_NEGATIVE':
            return t`Payment amount should be negative`;
        case 'JOURNAL_ENTRY_PAYMENT_AMOUNT_TOO_BIG':
            return t`Payment amount should be lower`;
        case 'JOURNAL_ENTRY_PAYMENT_AMOUNT_TOO_LOW':
            return t`Payment amount should be greater`;
        case 'JOURNAL_ENTRY_PAYMENT_PAYMENT_DATE_INVALID':
            return t`Payment date is invalid. Make sure payment date is set to today or later`;
        case 'JOURNAL_ENTRY_PAYMENT_NULL':
        case 'JOURNAL_ENTRY_PAYMENT_NOT_FOUND':
            return t`Could not find payment. Please refresh page and try again.`;
        case 'JOURNALENTRYPAYMENTDETAILSPAYMENTIDWRONGLENGTH':
            return t`It should have ${param} characters`;
        case 'JOURNALENTRYPAYMENTDETAILSPAYMENTIDTOOSHORT':
            return t`It should have minimum ${param} characters`;
        case 'JOURNALENTRYPAYMENTDETAILSPAYMENTIDTOOLONG':
            return t`It should have maximum ${param} characters`;
        case 'JOURNAL_ENTRY_PAYMENT_ID_INVALID':
        case 'JOURNALENTRYPAYMENTDETAILSPAYMENTIDDOESNOTMATCHSTRINGPATTERN':
            return t`Is invalid. Please try again`;
        case 'JOURNALENTRYPAYMENTDETAILSPAYMENTIDMUSTBENUMERIC':
            return t`It should be numeric`;
        case 'JOURNAL_ENTRY_PAYMENT_PAYMENT_SUPPLIER_IDENTIFIER_EMPTY':
            return t`Account number is empty`;
        case 'JOURNAL_ENTRY_PAYMENT_PAYMENT_SUPPLIER_INVOICE_IDENTIFIER_EMPTY':
            return t`OCR line/Message is empty`;
        case 'JOURNALENTRYPAYMENTDETAILSCREDITORIDDOESNOTMATCHSTRINGPATTERN':
            return t`Account No invalid`;
        case 'COULD_NOT_UPDATE_PAYMENT':
            return t`Something went wrong updating the payment. Please refresh and try again.`;
        case 'COULD_NOT_UPDATE_SUPPLIER':
            return t`Something went wrong updating the supplier. Please refresh and try again.`;
        case 'SupplierNumberChangeNotAllowed':
            return t`The supplier number cannot be changed.`;

        // REJECT PAYMENT
        case 'SMARTPAYPAYMENT_PAYMENT_REJECT_GENERIC_ERROR':
            return t`Something went wrong. Please try again. If it still happens, contact Support and provide this code: ${param}`;
        case 'SMARTPAYPAYMENT_PAYMENT_REJECT_EMPTY_REASON':
            return t`Reason is required.`;
        case 'SMARTPAYPAYMENT_PAYMENT_REJECT_INVALID_STATUS':
            return t`Cannot reject a payment with invalid status.`;
        case 'SMARTPAYPAYMENT_PAYMENT_REJECT_FAILED':
            return t`Error in rejecting the payment in November First. Please try again.`;

        default:
            return t`Something went wrong. Please try again`;
    }
};
// #endregion

// #region UI

export const getDefaultTableRowHeight = (): Table3RowHeight => {
    const w = window.innerWidth;
    const h = window.innerHeight;

    let rowDensity = 'medium';

    if (w < 1200 || h < 800) rowDensity = 'short';

    return rowDensity as Table3RowHeight;
};

export const getSmartPayBannerErrorText = (
    smartPayInfoErrorMessage,
    loadingInfo,
    loadingPaymentAccounts,
    loadingPaymentAccountAppSettings,
    selectedPaymentAccountId,
    loadingJournals,
    selectedJournal,
    loadingAccounts,
    smartPayInfo,
    accounts,
    isPaymentCreationEnabled
): { bannerState: State; bannerText: JSX.Element | string } | null => {
    if (!isPaymentCreationEnabled) {
        return {
            bannerState: 'error',
            bannerText: (
                <Trans>
                    <div className="flex flex-col">
                        <h4>Temporary Service Interruption</h4>
                        <span>
                            Smart Pay is undergoing a temporary interruption as we have identified a technical issue that we are
                            actively working to resolve. We prioritise your safety in every transaction, and therefore, payments
                            cannot be created or approved during this period. Our team is working diligently to address the issue.
                            We appreciate your understanding and patience. You can find more information on our{' '}
                            <a href="https://status.e-conomic.com/">status page</a>
                        </span>
                    </div>
                </Trans>
            ),
        };
    }

    if (smartPayInfoErrorMessage) {
        return { bannerState: 'error', bannerText: getPaymentErrorTranslation(smartPayInfoErrorMessage) };
    }

    if (!loadingInfo && !loadingPaymentAccounts && !loadingPaymentAccountAppSettings && selectedPaymentAccountId === undefined) {
        return { bannerState: 'error', bannerText: getPaymentErrorTranslation('SMARTPAY_INFO_MISSING_DATA') };
    }

    if (!loadingInfo && !loadingJournals && selectedJournal && isSelectedJournalInvalidForSmartPay(selectedJournal)) {
        return {
            bannerState: 'warning',
            bannerText: t`Your Smart Pay Setup has an invalid daybook. Please go to your Smart Pay settings and choose a valid daybook, or go to your daybook's setup and set 'Allowed entry types' to 'All'.`,
        };
    }

    if (!(loadingAccounts || loadingInfo || !smartPayInfo)) {
        const bankAccounts: number[] = [];
        if (smartPayInfo.feeAccount) {
            bankAccounts.push(smartPayInfo.feeAccount);
        }

        if (smartPayInfo.paymentAccount) {
            bankAccounts.push(smartPayInfo.paymentAccount);
        }

        if (
            !!bankAccounts[0] &&
            !!accounts.find(e => bankAccounts.includes(e.number) && !smartPayValidAccountTypes.includes(e.type.number))
        ) {
            return {
                bannerState: 'error',
                bannerText: t`Your Smart Pay Setup has an invalid account chosen. Please go to your Smart Pay settings and choose an account of type Profit and loss or Balance.`,
            };
        }
    }

    return null;
};

// #endregion

export const isForeignCurrency = (invoice: SmartPayPaymentExtendedModel): boolean => {
    return invoice.baseCurrency !== invoice.invoiceCurrency;
};

export const updateSmartPaySettings = async (payload: {
    PaymentAccount?: number | null;
    Daybook?: number | null;
    FeeAccount?: number | null;
}) => {
    try {
        const response = await internalApiClient.put('/smartpay/setting', payload);
        return response;
    } catch (error) {
        return error;
    }
};

export const getValidPaymentAccount = (paymentAccounts: SmartPayPaymentAccountModel[]) =>
    paymentAccounts.filter(acc => acc.number && acc.status === SmartPayPaymentAccountStatus.ActiveOS);

export const getPaymentsCountAndTotalByStatuses = (
    paymentsCount: SmartPayPaymentCountByStatusModel[],
    statuses: string[]
): CountAndTotal => {
    const data: CountAndTotal = {
        count: paymentsCount
            ? paymentsCount
                  .filter(paymentCount => statuses.includes(paymentCount.status))
                  .reduce((sum, item) => sum + item.count, 0)
            : 0,
        total: paymentsCount
            ? paymentsCount
                  .filter(paymentCount => statuses.includes(paymentCount.status))
                  .reduce((sum, item) => sum + item.total, 0)
            : 0,
    };
    return data;
};

export enum SmartpayPaymentTabs {
    Unpaid = 'Unpaid',
    PendingApproval = 'PendingApproval',
    Scheduled = 'Scheduled',
    Paid = 'Paid',
    OrphanedPayments = 'OrphanedPayments',
}

export const navigateToNovemberFirstApprovalPage = (url: string | undefined) => {
    if (!url) return;
    window.location.href = url;
};

export const getFilenameForExcelExport = (tab: SmartpayPaymentTabs) => {
    const prefix = t`Smart Pay`;
    const suffix =
        tab === SmartpayPaymentTabs.Unpaid
            ? t`Unpaid`
            : tab === SmartpayPaymentTabs.PendingApproval
            ? t`Pending Approval`
            : tab === SmartpayPaymentTabs.Scheduled
            ? t`Scheduled`
            : tab === SmartpayPaymentTabs.Paid
            ? t`Paid`
            : '';

    return `${prefix} - ${suffix} - ${format(new Date(), 'YYYY-MM-DD')}.xlsx`;
};
export const getColumnsForExcelExport = (tab: SmartpayPaymentTabs) => {
    const colums = [{ accessor: 'paymentDate', columnHeader: t`Payment Date` }];
    if (tab !== SmartpayPaymentTabs.Unpaid) colums.push({ accessor: 'createdDate', columnHeader: t`Creation Date` });
    if (tab === SmartpayPaymentTabs.Scheduled || tab === SmartpayPaymentTabs.Paid)
        colums.push({ accessor: 'settlementDate', columnHeader: t`Settlement date` });
    colums.push({ accessor: 'invoiceNumber', columnHeader: t`Invoice` });
    colums.push({ accessor: 'supplierId', columnHeader: t`Supplier no.` });
    colums.push({ accessor: 'supplierName', columnHeader: t`Supplier name` });
    if (tab !== SmartpayPaymentTabs.Unpaid)
        colums.push({ accessor: 'senderBankAccount', columnHeader: t`From Bank Account Number` });
    colums.push({ accessor: 'paymentSupplierIdentifier', columnHeader: t`To Bank Account Number` });
    if (tab === SmartpayPaymentTabs.Unpaid) colums.push({ accessor: 'amountToBePaid', columnHeader: t`Amount (Curr)` });
    else colums.push({ accessor: 'paidAmount', columnHeader: t`Amount (Curr)` });
    colums.push({ accessor: 'invoiceCurrency', columnHeader: t`Currency` });
    if (tab === SmartpayPaymentTabs.Unpaid) colums.push({ accessor: 'amountInBaseCurrency', columnHeader: t`Amount (DKK)` });
    else colums.push({ accessor: 'invoiceTotalInBaseCurrency', columnHeader: t`Amount (DKK)` });
    colums.push({ accessor: 'exchangeRate', columnHeader: t`Exchange rate` });
    colums.push({ accessor: 'fee', columnHeader: t`Fee` });
    if (tab !== SmartpayPaymentTabs.Unpaid) colums.push({ accessor: 'paymentReference', columnHeader: t`Reference` });
    colums.push({ accessor: 'status', columnHeader: t`Status` });

    return colums;
};

export const hasAccessToScannedDocuments = (agreement: Agreement): boolean =>
    agreement.modules.smartInbox || agreement.modules.scanning;

export const getSettingsModalApprovalTabAnchor = () => '#settingsModal-approvalTab';
